<template>
<div class="chat-assistant">
    <!-- Mensaje flotante -->
    <div class="floating-message" v-show="!isChatOpen">
        {{ currentMessage }}
    </div>

    <!-- Burbuja de chat -->
    <v-btn fab large color="#8061c2" class="chat-bubble" @click="toggleChat" elevation="4">
        <v-icon color="white">
            {{ isChatOpen ? 'mdi-close' : 'mdi-message' }}
        </v-icon>
    </v-btn>

    <!-- Ventana de chat -->
    <v-card v-show="isChatOpen" class="chat-window" elevation="8">
        <!-- Header del chat -->
        <v-card-title class="chat-header" style="background-color: rgb(128 97 194)">
            <v-avatar color="black" class="mr-3">
                <v-icon color="white">mdi-robot</v-icon>
            </v-avatar>
            <div class="white--text d-flex align-center">
                <div>
                    <div class="text-subtitle-1 font-weight-medium mb-0">{{ agent ? agent.name : 'Asistente Q&A' }}</div>
                    <div class="text-caption text-left mt-n1">{{ $t('chat.online') }}</div>
                </div>
            </div>
            <div class="ml-16 mt-n5">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            small
                            class="ml-12"
                            color="white"
                            v-bind="attrs"
                            v-on="on"
                            @click="isTypingEnabled = !isTypingEnabled"
                        >
                            <v-icon>{{ isTypingEnabled ? 'mdi-keyboard' : 'mdi-keyboard-off' }}</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ isTypingEnabled ? $t('chat.typingOn') : $t('chat.typingOff') }}</span>
                </v-tooltip>
            </div>
        </v-card-title>

        <!-- Área de mensajes -->
        <v-card-text class="chat-messages" ref="messageArea">
            <div v-for="(message, index) in messages" :key="index" :class="['message', message.type]">
                <div class="message-content">
                    <span v-html="formatMessage(message.text)"></span><span v-if="message.isTyping" class="typing-cursor"></span>
                </div>
            </div>

            <!-- Indicador de escritura -->
            <div v-if="typingMessage" class="message received">
                <div class="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
            <!-- Sugerencias iniciales -->
            <div v-if="messages.length === 1" class="suggestions-container">
                <div class="suggestions-title">{{ $t('chat.suggestion') }}</div>
                <v-btn v-for="(suggestion, index) in suggestions" :key="index" text rounded outlined class="suggestion-btn mb-2" color="#8061c2" @click="sendSuggestion(suggestion)">
                    {{ suggestion }}
                </v-btn>
            </div>
        </v-card-text>

        <!-- Área de input -->
        <v-card-actions class="chat-input">
            <v-text-field 
                v-model="newMessage" 
                :placeholder="$t('chat.placeholder')" 
                outlined 
                dense 
                hide-details 
                @keyup.enter="sendMessage" 
                class="mr-2"
                :rules="[v => v.length >= 2 || 'Mínimo 2 caracteres']"
                maxlength="50"
                counter
            ></v-text-field>
            <v-btn color="#8061c2" icon @click="sendMessage" :disabled="!newMessage.trim()">
                <v-icon>mdi-send</v-icon>
            </v-btn>
        </v-card-actions>
    </v-card>
</div>
</template>

<script>
import axios from '@/axios.js';

export default {
    name: 'ChatAssistant',
    data() {
        return {
            isTypingEnabled: true, // Control para activar/desactivar escritura gradual
            isTypingMessage: false,
            typingSpeed: 53, // velocidad de escritura en milisegundos
            isChatOpen: false,
            newMessage: '',
            messages: [],
            welcomeMessages: [
                this.$t('chat.welcome')
            ],
            currentMessage: '',
            messageInterval: null,
            suggestions: [
                this.$t('chat.suggestions.what'),
                this.$t('chat.suggestions.features'),
                this.$t('chat.suggestions.benefits')
            ],
            sessionId: null,
            agent: null,
            isLoading: false,
            typingMessage: false
        }
    },
    methods: {
        async typeMessage(fullText) {
            // Si isTypingEnabled es false, mostrar el mensaje completo inmediatamente
            if (!this.isTypingEnabled) {
                this.messages.push({
                    text: fullText,
                    type: 'received',
                    isTyping: false
                });
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
                return;
            }

            // Si isTypingEnabled es true, proceder con el efecto de escritura
            this.isTypingMessage = true;
            let displayText = '';
            const message = {
                text: displayText,
                type: 'received',
                isTyping: true
            };
            this.messages.push(message);

            for (let i = 0; i < fullText.length; i++) {
                if (!this.isTypingEnabled) {
                    // Si se desactiva durante la escritura, mostrar todo el texto
                    message.text = fullText;
                    message.isTyping = false;
                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, this.typingSpeed));
                displayText += fullText[i];
                message.text = displayText;
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            }
            message.isTyping = false;
            this.isTypingMessage = false;
        },
        formatMessage(text) {
            if (!text) return '';
            return text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;')
                .replace(/Harmony Fan/g, '**Harmony Fan**')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\n/g, '<br>');
        },
        async createSession() {
            try {
                // Verificar si ya existe una sesión guardada
                const savedSession = localStorage.getItem('chat_session');
                if (savedSession) {
                    const session = JSON.parse(savedSession);
                    this.sessionId = session.session_id;
                    this.agent = session.agent;
                    this.messages = session.messages || [];
                    
                    // Si hay mensajes guardados, no mostrar el mensaje de bienvenida
                    if (this.messages.length > 0) {
                        return true;
                    }
                }

                const user = JSON.parse(localStorage.getItem('user'));
                const response = await axios.post('/chat-create-session', {
                    user_id: user ? user.id : null
                });

                if (response.data && response.data.session_id) {
                    this.sessionId = response.data.session_id;
                    this.agent = response.data.agent;
                    
                    const welcomeEmojis = ['✨', '👋', '🤖', '💫', '💝'];
                    const randomEmoji = welcomeEmojis[Math.floor(Math.random() * welcomeEmojis.length)];
                    const welcomeMessage = {
                        text: `${randomEmoji} ${this.$t('chat.welcome')}`,
                        type: 'received'
                    };
                    
                    this.messages.push(welcomeMessage);
                    
                    // Guardar la sesión en localStorage
                    this.saveSessionToStorage();
                    
                    return true;
                } else {
                    console.error('Error: respuesta sin session_id:', response.data);
                    return false;
                }
            } catch (error) {
                console.error('Error creating chat session:', error);
            }
        },

        saveSessionToStorage() {
            const sessionData = {
                session_id: this.sessionId,
                agent: this.agent,
                messages: this.messages,
                last_updated: new Date().toISOString()
            };
            localStorage.setItem('chat_session', JSON.stringify(sessionData));
        },

        async toggleChat() {
            this.isChatOpen = !this.isChatOpen;
            if (this.isChatOpen && this.messages.length === 0) {
                await this.createSession();
            }
        },
        async sendMessage() {
            const message = this.newMessage.trim();
            if (message && message.length >= 2 && message.length <= 50 && !this.isLoading) {
                this.isTypingMessage = false; // Cancelar cualquier escritura en curso
                // Cancelar cualquier escritura en curso
                this.isTypingMessage = false;
                if (!this.sessionId) {
                    await this.createSession();
                }

                // Agregar mensaje del usuario al chat
                this.messages.push({
                    text: message,
                    type: 'sent'
                });
                
                // Guardar después de cada mensaje del usuario
                this.saveSessionToStorage();
                
                this.newMessage = '';
                this.$nextTick(() => {
                    this.scrollToBottom();
                });

                try {
                    this.isLoading = true;
                    this.typingMessage = true;

                    const user = JSON.parse(localStorage.getItem('user'));
                    const response = await axios.post('/chat-assistant-agent', {
                        session_id: this.sessionId,
                        user_id: user ? user.id : null,
                        message: message
                    });

                    this.typingMessage = false;

                    if (response.data.message && response.data.message.message) {
                        await this.addBotMessage(response.data.message.message);
                        // Guardar después de la respuesta del bot
                        this.saveSessionToStorage();
                    }
                } catch (error) {
                    console.error('Error sending message:', error);
                    this.typingMessage = false;
                    this.addBotMessage('Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo más tarde.');
                } finally {
                    this.isLoading = false;
                }
            }
        },
        async addBotMessage(text) {
            if (this.isTypingEnabled) {
                await this.typeMessage(text);
            } else {
                this.messages.push({
                    text: text,
                    type: 'received'
                });
            }
            this.$nextTick(() => {
                this.scrollToBottom();
            })
        },
        async sendSuggestion(suggestion) {
            if (!this.isLoading) {
                this.isTypingMessage = false; // Cancelar cualquier escritura en curso
                // Cancelar cualquier escritura en curso
                this.isTypingMessage = false;
                this.messages.push({
                    text: suggestion,
                    type: 'sent'
                });
                this.$nextTick(() => {
                    this.scrollToBottom();
                });

                try {
                    this.isLoading = true;
                    this.typingMessage = true;

                    const user = JSON.parse(localStorage.getItem('user'));
                    const response = await axios.post('/chat-assistant-agent', {
                        session_id: this.sessionId,
                        user_id: user ? user.id : null,
                        message: suggestion
                    });

                    this.typingMessage = false;

                    if (response.data.message && response.data.message.message) {
                        this.addBotMessage(response.data.message.message);
                    }
                } catch (error) {
                    console.error('Error sending suggestion:', error);
                    this.typingMessage = false;
                    this.addBotMessage('Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo más tarde.');
                } finally {
                    this.isLoading = false;
                }
            }
        },
        scrollToBottom() {
            const messageArea = this.$refs.messageArea
            if (messageArea) {
                messageArea.scrollTop = messageArea.scrollHeight
            }
        },
        rotateWelcomeMessages() {
            let index = 0
            this.currentMessage = this.welcomeMessages[0]
            this.messageInterval = setInterval(() => {
                index = (index + 1) % this.welcomeMessages.length
                this.currentMessage = this.welcomeMessages[index]
            }, 5000)
        }
    },
    mounted() {
        this.rotateWelcomeMessages()
    },
    beforeDestroy() {
        if (this.messageInterval) {
            clearInterval(this.messageInterval)
        }
    }
}
</script>

<style scoped>
.chat-assistant {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1;
}

.chat-bubble {
    position: relative;
    z-index: 2;
}

.floating-message {
    position: absolute;
    bottom: 80px;
    right: 0;
    background-color: white;
    padding: 12px 20px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    min-width: 280px;
    max-width: 320px;
    animation: float 0.5s ease-out;
    font-size: 15px;
}

.floating-message::after {
    content: '';
    position: absolute;
    bottom: -8px;
    right: 20px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
}

.chat-window {
    position: fixed;
    bottom: 90px;
    right: 20px;
    width: 350px;
    height: 500px;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    overflow: hidden;
}

.chat-header {
    padding: 10px 20px;
}

.chat-messages {
    flex-grow: 1;
    overflow-y: auto;
    padding: 20px;
    background-color: #efeae2;
    background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAABmJLR0QA/wD/AP+gvaeTAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH4QgJDh07tObANwAAAB1pVFh0Q29tbWVudAAAAAAAQ3JlYXRlZCB3aXRoIEdJTVBkLmUHAAABrUlEQVRo3u2ZTU7DMBCFPywo4hB0zR3ohlNwAW7ABbgDJ+h9uEE3bCoWLBAs4BBYmYyUKE7sxPbYTt4qqqJ4bM+b+bERAsMwagOYD5LZBVqgT0SHiOg3nxP+jUcDrIEucCwwwBq4A6b+Pc+ACiwDw3TsmoAKLICVcN0HZhkB5IkBDoL7lO7dJQBjgE8F4DsySPFXS7AFPAG1QE0A98BM6Z5VZD2JAI7Ayn3nFrjMFA5r4NEBOyVBZQByF8H9BnwAd5HPlcDcecKGxCBA7kOqdbV3DWx8LlgCVwHZV8AbA1UjHlMAuQDunbxugLlQvxbAh5MeQDPgmQtgJ6xP2QA7YAk8KFSoLfDi6mVlMsIh3+5G6RKRb0GXiJb+KRsaQBUanyr2GnFPDSBKI+6pAYRrJEbrVQSIRkZMIyajEZORgQ4/rVXUDGQa6YBp4aOIdEQ2FVj4kHGkxqICi4rYol6m+hhp+L2wWVZg4UO29IUP4UasWwU1cqz9VlxvgXflFs3XdUTvLR7eSu3L7UZ0U+8d2KTeZ2IXfZt674l/t9llHkk63QnzZPQ/In4BmqV5/mBKCswAAAAASUVORK5CYII=');
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.message {
    display: block;
    margin-bottom: 10px;
    width: 100%;
    clear: both;
}

.message.sent {
    justify-content: flex-end;
}

.message.received {
    justify-content: flex-start;
}

.message-content {
    padding: 8px 12px;
    border-radius: 15px;
    word-wrap: break-word;
    display: inline-block;
    text-align: left;
    white-space: pre-wrap;
}

.sent .message-content {
    background-color: #8061c2;
    color: white;
    border-top-right-radius: 5px;
    max-width: 80%;
    float: right;
    clear: both;
}

.received .message-content {
    background-color: white;
    color: #333;
    border-top-left-radius: 5px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    max-width: 80%;
    float: left;
    clear: both;
}

.chat-input {
    padding: 10px;
    background-color: #f0f0f0;
    border-top: 1px solid #e0e0e0;
}

.chat-input .v-text-field.v-text-field--enclosed .v-text-field__details {
    display: none;
}

.chat-input .v-text-field.v-text-field--enclosed.v-input--dense .v-text-field__details {
    display: none;
}

.chat-input .v-text-field.v-text-field--enclosed .v-text-field__slot {
    background-color: white;
    border-radius: 20px;
    margin-bottom: 0;
}

@keyframes float {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes slideInRight {
    from {
        opacity: 0;
        transform: translateX(20px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.message.received {
    justify-content: flex-start;
    animation: slideIn 0.3s ease-out;
}

.message.sent {
    justify-content: flex-end;
    animation: slideInRight 0.3s ease-out;
}

.floating-message {
    animation: float 0.5s ease-out;
    transition: all 0.3s ease;
}

.floating-message:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.typing-indicator {
    display: flex;
    padding: 8px;
    width: 70px;
    justify-content: center;
    background-color: white;
    border-radius: 15px;
    margin: 10px 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.typing-indicator span {
    width: 8px;
    height: 8px;
    background-color: #8061c2;
    border-radius: 50%;
    margin: 0 2px;
    animation: bounce 1.3s linear infinite;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.15s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.3s;
}

@keyframes bounce {

    0%,
    60%,
    100% {
        transform: translateY(0);
    }

    30% {
        transform: translateY(-4px);
    }
}

.suggestions-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 10px;
    margin-top: 10px;
}

.suggestions-title {
    font-size: 0.875rem;
    color: #999999;
    margin-bottom: 8px;
    padding-left: 4px;
    font-weight: 500;
}

.suggestion-btn {
    margin: 4px 0;
    text-transform: none !important;
    font-weight: normal !important;
    border: 1px solid #8061c2 !important;
    background-color: white !important;
    transition: all 0.3s ease;
}

.suggestion-btn:hover {
    background-color: #f8f5ff !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.typing-cursor {
    display: inline-block;
    width: 2px;
    height: 1em;
    background-color: #333;
    margin-left: 1px;
    vertical-align: middle;
    animation: blink 1s infinite;
    position: relative;
    top: -1px;
}

@keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
}
</style>
